import type { Vector } from "@common/utils/vector";
import { GameConsole } from "../console/gameConsole";
import { CombatSounds } from "./combatSounds";

/**
 * In-match feedback, drawn as a light DOM/canvas overlay above the game:
 *  - hit markers: an X flashes at the crosshair on every hit you land (red and bigger on a kill, steel grey on armour),
 *    with its sound (combatSounds.ts: a crisp tick, a metal tink on armour, a heavy thunk and ring on a kill)
 *  - damage direction: a red arc at the screen edge points at whatever hurt you, and keeps pointing at it as you move
 *  - low-health warning: red edges pulse with a heartbeat below 25% health
 * Each has its own setting (Settings > Interface).
 */

interface Arc { readonly source: Vector, age: number, readonly life: number, strength: number }

const ARC_LIFE = 1.5;          // seconds an arc stays visible
const ARC_SPREAD = 0.42;       // half-width of an arc, radians
const LOW_HEALTH = 0.25;       // fraction of max health

class CombatFeedbackManager {
    private _root?: HTMLDivElement;
    private _marker?: HTMLDivElement;
    private _canvas?: HTMLCanvasElement;
    private _vignette?: HTMLDivElement;
    private _arcs: Arc[] = [];
    private _audio?: AudioContext;
    private _mouse = { x: -1, y: -1 };
    private _lowHealth = false;
    private _heartbeatTimer?: ReturnType<typeof setInterval>;
    private _markerTimer?: ReturnType<typeof setTimeout>;

    private _setting(name: "cv_hit_markers" | "cv_damage_direction" | "cv_low_health_warning"): boolean {
        try { return Boolean(GameConsole.getBuiltInCVar(name)); } catch { return true; }
    }

    private _volume(): number {
        try {
            return Number(GameConsole.getBuiltInCVar("cv_master_volume")) * Number(GameConsole.getBuiltInCVar("cv_sfx_volume"));
        } catch { return 0.6; }
    }

    private _ensure(): void {
        if (this._root) return;
        const root = document.createElement("div");
        root.id = "combat-feedback";
        const canvas = document.createElement("canvas");
        canvas.id = "damage-direction";
        const vignette = document.createElement("div");
        vignette.id = "low-health-vignette";
        const marker = document.createElement("div");
        marker.id = "hit-marker";
        for (let i = 0; i < 4; i++) marker.appendChild(document.createElement("i"));
        root.append(vignette, canvas, marker);
        document.body.appendChild(root);
        this._root = root; this._canvas = canvas; this._vignette = vignette; this._marker = marker;
        window.addEventListener("pointermove", event => { this._mouse = { x: event.clientX, y: event.clientY }; }, { passive: true });
        const unlock = (): void => {
            try { this._audio ??= new AudioContext(); if (this._audio.state === "suspended") void this._audio.resume(); } catch { /* no audio */ }
        };
        window.addEventListener("pointerdown", unlock, { passive: true });
        window.addEventListener("keydown", unlock, { passive: true });
    }

    // ---------------------------------------------------------------- hit markers
    /** `armored`: what you hit wore armour that shows (it took part of the hit) */
    hit(kill: boolean, armored = false): void {
        if (!this._setting("cv_hit_markers")) return;
        this._ensure();
        const marker = this._marker!;
        const x = this._mouse.x >= 0 ? this._mouse.x : window.innerWidth / 2;
        const y = this._mouse.y >= 0 ? this._mouse.y : window.innerHeight / 2;
        marker.style.left = `${x}px`;
        marker.style.top = `${y}px`;
        // restart the animation by rewinding it (taking the class off and on again needs a forced layout of the whole
        // page, on every hit); a marker that isn't showing, or switches to the kill marker, starts its own animation
        if (marker.classList.contains("show") && marker.classList.contains("kill") === kill && typeof marker.getAnimations === "function") {
            for (const animation of marker.getAnimations()) animation.currentTime = 0;
        } else {
            marker.classList.add("show");
            marker.classList.toggle("kill", kill);
        }
        marker.classList.toggle("armor", armored && !kill);
        if (this._markerTimer) clearTimeout(this._markerTimer);
        this._markerTimer = setTimeout(() => marker.classList.remove("show", "kill", "armor"), kill ? 420 : 220);
        // the designed sounds, or (before they're loaded) the old synthesised tick
        if (!CombatSounds.confirm(kill ? "kill" : armored ? "armor" : "hit")) this._tick(kill);
    }

    private _tick(kill: boolean): void {
        const ctx = this._audio;
        const volume = this._volume();
        if (!ctx || !(volume > 0)) return;
        const t = ctx.currentTime;
        const blip = (freq: number, at: number, gainValue: number, length: number): void => {
            const osc = ctx.createOscillator(); const gain = ctx.createGain();
            osc.type = "triangle";
            osc.frequency.setValueAtTime(freq, t + at);
            osc.frequency.exponentialRampToValueAtTime(freq * 0.7, t + at + length);
            gain.gain.setValueAtTime(0.0001, t + at);
            gain.gain.exponentialRampToValueAtTime(gainValue * volume, t + at + 0.004);
            gain.gain.exponentialRampToValueAtTime(0.0001, t + at + length);
            osc.connect(gain).connect(ctx.destination);
            osc.start(t + at); osc.stop(t + at + length + 0.02);
        };
        if (kill) { blip(1150, 0, 0.16, 0.07); blip(760, 0.06, 0.14, 0.12); }
        else blip(1650, 0, 0.09, 0.045);
    }

    // ---------------------------------------------------------------- damage direction
    damagedFrom(sources: readonly Vector[]): void {
        if (!this._setting("cv_damage_direction") || !sources.length) return;
        this._ensure();
        for (const source of sources) {
            // hits from the same spot refresh one arc instead of stacking many
            const same = this._arcs.find(arc => Math.hypot(arc.source.x - source.x, arc.source.y - source.y) < 3);
            if (same) { same.age = 0; same.strength = Math.min(1, same.strength + 0.25); continue; }
            if (this._arcs.length >= 8) this._arcs.shift();
            this._arcs.push({ source: { x: source.x, y: source.y }, age: 0, life: ARC_LIFE, strength: 0.75 });
        }
    }

    /** the overlay canvas has something on it (an idle one is left alone: clearing a full-screen canvas every frame costs) */
    private _drawn = false;

    /** called every frame with the position of the player the camera follows */
    update(deltaMS: number, me: Vector | undefined): void {
        const canvas = this._canvas;
        if (!canvas) return;
        if (!me) this._arcs = [];
        if (!this._arcs.length && !this._drawn) return;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const w = window.innerWidth, h = window.innerHeight;
        if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
            canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
        }
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);
        this._drawn = false;
        if (!this._arcs.length || !me) return;
        this._drawn = true;
        const dt = Math.min(deltaMS, 60) / 1000;
        const cx = w / 2, cy = h / 2;
        const radius = Math.min(w, h) * 0.34;
        ctx.lineCap = "round";
        for (let i = this._arcs.length - 1; i >= 0; i--) {
            const arc = this._arcs[i];
            arc.age += dt;
            if (arc.age >= arc.life) { this._arcs.splice(i, 1); continue; }
            const angle = Math.atan2(arc.source.y - me.y, arc.source.x - me.x);
            const fade = arc.age < 0.08 ? arc.age / 0.08 : 1 - (arc.age - 0.08) / (arc.life - 0.08);
            const alpha = Math.max(0, fade) * (0.45 + 0.55 * arc.strength);
            // soft outer glow (wide faint strokes: a canvas shadow blur looks the same but is slow to draw every frame),
            // then a crisp core that tapers at both ends
            for (const [width, glow] of [[58, 0.07], [44, 0.11], [32, 0.16]] as const) {
                ctx.strokeStyle = `rgba(255, 40, 30, ${glow * alpha})`;
                ctx.lineWidth = width;
                ctx.beginPath(); ctx.arc(cx, cy, radius, angle - ARC_SPREAD, angle + ARC_SPREAD); ctx.stroke();
            }
            ctx.strokeStyle = `rgba(200, 20, 20, ${0.35 * alpha})`;
            ctx.lineWidth = 26;
            ctx.beginPath(); ctx.arc(cx, cy, radius, angle - ARC_SPREAD, angle + ARC_SPREAD); ctx.stroke();
            const steps = 14;
            for (let s = 0; s < steps; s++) {
                const a0 = angle - ARC_SPREAD + (2 * ARC_SPREAD * s) / steps;
                const a1 = a0 + (2 * ARC_SPREAD) / steps + 0.01;
                const mid = 1 - Math.abs((s + 0.5) / steps * 2 - 1); // 0 at the ends, 1 in the middle
                ctx.strokeStyle = `rgba(255, ${70 + 60 * mid}, ${55 + 30 * mid}, ${alpha * (0.25 + 0.75 * mid)})`;
                ctx.lineWidth = 4 + 7 * mid;
                ctx.beginPath(); ctx.arc(cx, cy, radius, a0, a1); ctx.stroke();
            }
            // a small chevron at the centre of the arc pointing outwards
            const px = cx + Math.cos(angle) * (radius + 16), py = cy + Math.sin(angle) * (radius + 16);
            ctx.save();
            ctx.translate(px, py); ctx.rotate(angle);
            ctx.fillStyle = `rgba(255, 110, 90, ${alpha})`;
            ctx.beginPath(); ctx.moveTo(8, 0); ctx.lineTo(-5, -7); ctx.lineTo(-2, 0); ctx.lineTo(-5, 7); ctx.closePath(); ctx.fill();
            ctx.restore();
        }
    }

    // ---------------------------------------------------------------- low health
    setHealth(normalized: number, alive: boolean): void {
        this._ensure();
        const enabled = this._setting("cv_low_health_warning");
        const low = enabled && alive && normalized > 0 && normalized <= LOW_HEALTH;
        const vignette = this._vignette!;
        if (low) {
            // stronger the closer you are to death
            const intensity = 0.45 + 0.55 * (1 - normalized / LOW_HEALTH);
            vignette.style.setProperty("--lh", intensity.toFixed(2));
        }
        if (low === this._lowHealth) return;
        this._lowHealth = low;
        vignette.classList.toggle("active", low);
        if (low) {
            this._heartbeat();
            this._heartbeatTimer = setInterval(() => this._heartbeat(), 900);
        } else if (this._heartbeatTimer) {
            clearInterval(this._heartbeatTimer);
            this._heartbeatTimer = undefined;
        }
    }

    private _heartbeat(): void {
        const ctx = this._audio;
        const volume = this._volume();
        if (!ctx || !(volume > 0)) return;
        const t = ctx.currentTime;
        const thump = (at: number, freq: number, gainValue: number): void => {
            const osc = ctx.createOscillator(); const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.setValueAtTime(freq, t + at);
            osc.frequency.exponentialRampToValueAtTime(freq * 0.55, t + at + 0.16);
            gain.gain.setValueAtTime(0.0001, t + at);
            gain.gain.exponentialRampToValueAtTime(gainValue * volume, t + at + 0.015);
            gain.gain.exponentialRampToValueAtTime(0.0001, t + at + 0.2);
            osc.connect(gain).connect(ctx.destination);
            osc.start(t + at); osc.stop(t + at + 0.22);
        };
        thump(0, 78, 0.5);
        thump(0.2, 64, 0.38);
    }

    /** leave the match: clear everything */
    reset(): void {
        this._arcs = [];
        this.setHealth(1, false);
        this._marker?.classList.remove("show", "kill", "armor");
        const ctx = this._canvas?.getContext("2d");
        if (ctx && this._canvas) ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
    }
}

export const CombatFeedback = new CombatFeedbackManager();
